import { usePeopleManagementApi } from 'infrastructure/api';
import { LoadingParams } from 'framework/list';
import { ComplianceDataType, MapedPoliciesDataType, PoliciesDataType } from './types';
import { ErrorMessage, useMessageService } from 'framework/ui/Message';
import { downloadFile } from 'framework/utils';
import { COMPLIANCE_DOCUMENT_TYPES } from '../registry/constants';
import { buildComplianceSearchPayload } from './complianceReportHelpers';

export const squashPoliciesByType = (data: ComplianceDataType[]) => {
  return data.map(employee => {
    // Grouping policies by document identity with safe fallback during rollout.
    const policiesByType = employee.policies.reduce(
      (acc, policy) => {
        const type = policy.complianceName;
        if (!acc[type]) {
          acc[type] = [];
        }
        acc[type].push(policy);
        return acc;
      },
      {} as Record<string, PoliciesDataType[]>,
    );

    // For each policy type, find the newest version and put the rest in previousVersions
    const squashedPolicies: MapedPoliciesDataType[] = Object.values(policiesByType).map(policies => {
      // Sort policies by version (descending)
      const sorted = [...policies].sort((a, b) => parseInt(b.policyVersion, 10) - parseInt(a.policyVersion, 10));

      // Actual
      const latest = sorted[0];

      // prev
      const previous = sorted.slice(1);

      return {
        ...latest,
        complianceType: latest.complianceType || COMPLIANCE_DOCUMENT_TYPES.policy,
        previousVersions: previous.length > 0 ? previous : [],
      };
    });

    return {
      ...employee,
      policies: squashedPolicies,
    };
  });
};

const useComplianceReportService = () => {
  const { sendPostRequest } = usePeopleManagementApi();
  const { addMessage } = useMessageService();

  const search = async (requestParams: LoadingParams) => {
    try {
      const payload = buildComplianceSearchPayload(requestParams);
      const request = await sendPostRequest('/people/compliance/info', payload);

      const response = await request.json();

      return {
        ...response,
        result: squashPoliciesByType(response.result),
      };
    } catch {
      return {
        result: [],
      };
    }
  };

  // The current user's own compliancies, from what people-management already stores: one request,
  // not the full report (which re-evaluates every compliance's audience per call).
  const getMyCompliancies = async (): Promise<MapedPoliciesDataType[]> => {
    try {
      const request = await sendPostRequest('/compliancies/me', {});
      const policies = (await request.json()) as PoliciesDataType[];

      return squashPoliciesByType([{ policies } as ComplianceDataType])[0]?.policies ?? [];
    } catch {
      return [];
    }
  };

  const downloadPdfReport = async (requestParams: LoadingParams, isHistoryIncluded: boolean, language: string) => {
    try {
      const payload = buildComplianceSearchPayload(requestParams);

      const response = await sendPostRequest('/people/compliance/pdf', {
        ...payload,
        isHistoryIncluded,
        language,
      });

      if (!response) {
        return;
      }

      return downloadFile(response, 'compliance_report.pdf', true);
    } catch (err) {
      addMessage(new ErrorMessage('Error downloading PDF'));
    }
  };

  return {
    search,
    getMyCompliancies,
    downloadPdfReport,
  };
};

export default useComplianceReportService;
